import { Injectable, OnDestroy, Provider, Type, inject } from '@angular/core';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { Status } from '@df/core/status';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { BasketService } from '@df/session/api/basket/basket.service';
import { Nexus } from '@tomandco/nexus-api-types';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { NProductConfiguration } from '../df-module-product-configuration.interfaces';
import { ProductConfigurationBaseService } from './base/product-configuration-base.service';
import {
    ProductConfigurationCustomOptionsService,
    TCustomOption
} from './simple/custom-options/product-configuration-configurable-product-data.service';

export interface IProductConfigurationOptions {
    /**
     * If yes then url and other meta of the whole page will be updated
     */
    metaIntegration: boolean;
}

@Injectable()
export abstract class ProductConfiguration implements OnDestroy {
    /**
     * Plugins
     */
    static readonly plugins: Type<NProductConfiguration.IPlugin>[] = [];

    /**
     * Requested product qty
     */
    readonly qty$ = new BehaviorSubject(1);

    /**
     * Source product
     */
    readonly product$ = new BehaviorSubject<Product<IProductDataV3>>(undefined as any);

    /**
     * Basket item (if config is by item)
     */
    readonly basketItem$ = new BehaviorSubject<BasketItem | undefined>(undefined);

    /**
     * Selected product change event
     * That DOES NOT include changes of custom options as they do not affect stock
     * To track custom options change either listen to customOptionsDataChange$ or priceChange$
     */
    readonly productRequestChange$ = new Subject<void>();

    /**
     * Price change
     */
    readonly priceChange$ = new Subject<void>();

    /**
     * Custom options model
     */
    readonly customOptions$ = new BehaviorSubject<TCustomOption[] | undefined>(undefined);

    /**
     * Custom options data mutation
     */
    readonly customOptionsDataChange$ = new Subject<TCustomOption>();

    /**
     * Submit
     */
    readonly submitBefore$ = new Subject<Nexus.Lib.Basket.BasketRequestData>();

    /**
     * Configuration type
     */
    abstract readonly type: string;

    /**
     * Subscriptions
     */
    protected subscriptions = new Subscription();

    /**
     * Basket service
     */
    private basketService = inject(BasketService);

    /**
     * Custom options service
     */
    readonly customOptionsService = inject(ProductConfigurationCustomOptionsService);

    /**
     * Custom options service
     */
    readonly baseService = inject(ProductConfigurationBaseService);

    /**
     * Basket request create event triggered every time basket request is created
     */
    readonly basketRequestCreate$ = new Subject<Nexus.Lib.Basket.BasketRequestData>();

    /**
     * Buffer for basket request
     */
    private basketRequestCache?: Nexus.Lib.Basket.BasketRequestData;

    /**
     * Product configuration options
     */
    protected options!: IProductConfigurationOptions;

    constructor() {
        ProductConfiguration.plugins.forEach(plugin => inject(plugin).init(this));
    }

    /**
     * Init configuration
     */
    init(product: Product<IProductDataV3>, options: IProductConfigurationOptions) {
        let initiated = false;
        this.options = options;

        this.baseService.init(this);

        const priceChange = () => {
            if (initiated) {
                this.basketRequestCache = undefined;
                this.priceChange$.next();
            }
        };

        this.productRequestChange$.subscribe(priceChange);
        this.qty$.subscribe(priceChange);
        this.customOptionsDataChange$.subscribe(option => {
            priceChange();
            // only trigger for required options
            if (option.data.req) {
                this.productRequestChange$.next();
            }
        });
        this.product$.next(product);
        this.product$.subscribe(product => this.onProductChange(product));
        initiated = true;
    }

    byItem(item: BasketItem): void {
        this.basketItem$.next(item);
        this.setProduct(item.product!);
        this.qty = item.qty;
    }

    /**
     * Sync data on product change
     */
    protected onProductChange(product?: Product<IProductDataV3>) {
        const options = product ? this.customOptionsService.getOptions(product, this) : undefined;
        if (this.customOptions$.value !== options) {
            this.customOptions$.next(options);
        }
        this.productRequestChange$.next();
    }

    /**
     * If product has custom options
     */
    get hasCustomOptions(): boolean {
        return !!this.customOptions$.value;
    }

    get qty(): number {
        return this.qty$.value;
    }

    set qty(value: number) {
        this.qty$.next(value);
    }

    /**
     * Cleanup
     */
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Add product in current configuration to basket
     */
    async addToBasket(status: Status | undefined): Promise<void> {
        const request = this.toBasketRequest();
        this.submitBefore$.next(request);
        await this.basketService.add(status, request);
    }

    /**
     * Configuration warning getter
     */
    validate(): NProductConfiguration.Error.Entry[] | undefined {
        if (!this.isSalable()) {
            return [{ code: NProductConfiguration.Error.Code.outOfStock, level: NProductConfiguration.Error.Level.error }];
        }
        return this.customOptionsService.validate(this.customOptions$.value);
    }

    /**
     * Basket request representations of the current selection
     */
    toBasketRequest(): Nexus.Lib.Basket.BasketRequestData {
        if (!this.basketRequestCache) {
            const request = this.baseService.toBasketRequest();
            this.basketRequestCreate$.next(request);
            this.basketRequestCache = request;
        }
        return this.basketRequestCache!;
    }

    /**
     * Checks if product is salable for given qty (in stock + backorders)
     * NOTE THAT HERE WE CHECK INCLUDING EXISTING BASKET CONTENTS
     */
    isSalable() {
        return this.basketService.isSalable(this.toBasketRequest());
    }

    /**
     * Checks if product is in stock (skipping backorders) for given qty
     * NOTE THAT HERE WE CHECK INCLUDING EXISTING BASKET CONTENTS
     */
    isInStock(): boolean {
        return this.basketService.isInStock(this.toBasketRequest());
    }

    /**
     * Returns max selable qty
     * NOTE THAT HERE WE CHECK INCLUDING EXISTING BASKET CONTENTS
     * @return {number}
     */
    maxQty(withBasket = true): number {
        return this.basketService.maxQty(this.toBasketRequest(), this.qty$.value, withBasket);
    }
    /**
     * Returns max in-stock qty (skipping backorders)
     * NOTE THAT HERE WE CHECK INCLUDING EXISTING BASKET CONTENTS
     * @return {number}
     */
    maxInQty(withBasket = true): number {
        return this.basketService.maxInQty(this.toBasketRequest(), this.qty$.value, withBasket);
    }

    /**
     * Retrieve price
     */
    price(): number {
        return this.product$.value.getPrice(this.toBasketRequest());
    }

    /**
     * Retrieve original price
     */
    orgPrice(): number | undefined {
        return this.product$.value.getOrgPrice(this.toBasketRequest());
    }

    /**
     * Retrieve row price
     */
    rowPrice(): number {
        return this.toBasketRequest().qty * this.price();
    }

    /**
     * Retrieve row original price
     */
    rowOrgPrice(): number | undefined {
        const orgPrice = this.orgPrice();
        return orgPrice ? this.toBasketRequest().qty * orgPrice : orgPrice;
    }

    /**
     * If current price is min price of and may change after configs
     */
    isFromPrice(): boolean {
        return !!this.product$.value.data?.is_from_price;
    }

    /**
     * Get all products that will be added to basket
     */
    getSelectedProducts(): { product: Product<IProductDataV3>; qty: number }[] {
        return [{ product: this.product$.value, qty: 1 }];
    }

    /**
     * Gets configuration link
     */
    getUrl(): string | undefined {
        return this.product$.value?.data?.url;
    }

    /**
     * Helper method for product setting
     */
    protected setProduct(product: Product<IProductDataV3>) {
        if (this.product$.value !== product) {
            if (!product) {
                console.warn('change prod to empty', product);
            }
            this.product$.next(product);
        }
    }
}
